import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Calendar,
  Clock,
  ArrowRight,
  Sparkles,
  Tag,
  TrendingUp,
  Filter,
  Heart,
  Globe,
  ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4100";

const BlogPage = () => {
  const navigate = useNavigate();

  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [focusedSearch, setFocusedSearch] = useState(false);

  useEffect(() => {
    fetchBlogs();
    fetchCategories();
    fetchTags();
  }, [currentPage, selectedCategory]);

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: 9,
        category: selectedCategory !== 'all' ? selectedCategory : undefined
      };
      const res = await axios.get(`${API_URL}/admin/public/blogs`, { params });
      if (res.data.success) {
        setBlogs(res.data.data);
        setFilteredBlogs(res.data.data);
        setTotalPages(res.data.pagination.totalPages);
      }
    } catch (error) {
      console.error("Error fetching blogs:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/public/categories`);
      if (res.data.success) setCategories(res.data.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchTags = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/public/tags`);
      if (res.data.success) setTags(res.data.data.slice(0, 10));
    } catch (error) {
      console.error("Error fetching tags:", error);
    }
  };

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      setFilteredBlogs(blogs);
      return;
    }
    const filtered = blogs.filter(blog =>
      blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      blog.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (blog.tags && blog.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
    );
    setFilteredBlogs(filtered);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100, damping: 12 }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 overflow-x-hidden selection:bg-indigo-500 selection:text-white">

      {/* ================= HERO SECTION ================= */}
      <div className="relative bg-indigo-900 pt-32 pb-24 lg:pt-48 lg:pb-32 overflow-hidden">
        {/* Animated Background Mesh */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 z-0"></div>
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(#ffffff33_1px,transparent_1px)] [background-size:20px_20px] z-0 pointer-events-none"></div>

        {/* Glowing Orbs */}
        <motion.div
          animate={{ x: [0, 100, 0], y: [0, -50, 0], opacity: [0.4, 0.6, 0.4] }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-0 right-0 w-[500px] h-[500px] bg-purple-500 rounded-full mix-blend-screen filter blur-[100px] opacity-40 z-0"
        />
        <motion.div
          animate={{ x: [0, -70, 0], y: [0, 50, 0], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          className="absolute bottom-0 left-10 w-[400px] h-[400px] bg-sky-500 rounded-full mix-blend-screen filter blur-[100px] opacity-30 z-0"
        />

        <div className="relative max-w-7xl mx-auto px-6 z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block py-1.5 px-4 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-indigo-200 text-sm font-semibold tracking-wide mb-6">
              <Sparkles className="w-4 h-4 inline mr-2 text-yellow-300" />
              Latest Insights & Stories
            </span>
            <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-6 tracking-tight leading-tight">
              Where <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-purple-400">Technology</span> Meets <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-orange-400">Future</span>
            </h1>
            <p className="text-lg md:text-xl text-indigo-100 max-w-2xl mx-auto mb-10 leading-relaxed font-light">
              Discover breakthrough ideas, tutorials, and deep dives into the world of software engineering and design.
            </p>
          </motion.div>

          {/* Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className={`relative max-w-2xl mx-auto transition-all duration-300 transform ${focusedSearch ? 'scale-105' : 'scale-100'}`}
          >
            <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
              <Search className={`w-5 h-5 ${focusedSearch ? 'text-indigo-500' : 'text-slate-400'}`} />
            </div>
            <input
              type="text"
              placeholder="Search for articles, news, or tutorials..."
              className="w-full pl-12 pr-4 py-4 rounded-2xl bg-white shadow-2xl shadow-indigo-900/20 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-4 focus:ring-indigo-500/30 transition-shadow text-lg"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setFocusedSearch(true)}
              onBlur={() => setFocusedSearch(false)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 -mt-10 relative z-20">
        <div className="flex flex-col lg:flex-row gap-8">

          {/* ================= SIDEBAR ================= */}
          <aside className="lg:w-1/4 space-y-8">
            {/* Categories */}
            <div className="bg-white rounded-2xl p-6 shadow-xl shadow-slate-200/50 border border-slate-100">
              <div className="flex items-center gap-2 mb-6 text-indigo-600">
                <Filter className="w-5 h-5" />
                <h3 className="font-bold text-lg">Browse Topics</h3>
              </div>
              <div className="space-y-2">
                <button
                  onClick={() => setSelectedCategory('all')}
                  className={`w-full text-left px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-200 flex justify-between items-center ${selectedCategory === 'all' ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/30' : 'bg-slate-50 text-slate-600 hover:bg-slate-100 hover:text-indigo-600'}`}
                >
                  <span>All Articles</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${selectedCategory === 'all' ? 'bg-white/20 text-white' : 'bg-white text-slate-400'}`}>{blogs.length}</span>
                </button>
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`w-full text-left px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-200 flex justify-between items-center ${selectedCategory === cat ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/30' : 'bg-slate-50 text-slate-600 hover:bg-slate-100 hover:text-indigo-600'}`}
                  >
                    <span>{cat}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${selectedCategory === cat ? 'bg-white/20 text-white' : 'bg-white text-slate-400'}`}>{blogs.filter(b => b.category === cat).length}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Trending Tags */}
            <div className="bg-white rounded-2xl p-6 shadow-xl shadow-slate-200/50 border border-slate-100">
              <div className="flex items-center gap-2 mb-6 text-pink-500">
                <TrendingUp className="w-5 h-5" />
                <h3 className="font-bold text-lg">Trending Now</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag, i) => (
                  <button
                    key={i}
                    onClick={() => { setSearchTerm(tag); handleSearch(); }}
                    className="px-3 py-1.5 rounded-lg bg-pink-50 text-pink-600 text-xs font-bold hover:bg-pink-100 transition-colors border border-pink-100"
                  >
                    #{tag}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* ================= MAIN GRID ================= */}
          <main className="lg:w-3/4">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="bg-white rounded-3xl h-[400px] animate-pulse shadow-lg"></div>
                ))}
              </div>
            ) : (
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-1 md:grid-cols-2 gap-8"
              >
                <AnimatePresence mode="popLayout">
                  {filteredBlogs.map(blog => (
                    <Link
                      key={blog._id}
                      to={`/blog/${blog.slug || blog._id}`}
                      className="block h-full"
                    >
                      <motion.article
                        layout
                        variants={itemVariants}
                        whileHover={{ y: -10, transition: { duration: 0.3 } }}
                        className="group bg-white rounded-3xl overflow-hidden shadow-xl shadow-slate-200/60 border border-slate-100 flex flex-col h-full cursor-pointer relative hover:shadow-2xl hover:shadow-indigo-500/10 transition-shadow duration-300"
                      >
                        {/* Image */}
                        <div className="relative h-64 overflow-hidden">
                          {blog.image ? (
                            <motion.img
                              src={blog.image.startsWith('http') ? blog.image : `${API_URL}/uploads/${blog.image}`}
                              className="w-full h-full object-cover"
                              whileHover={{ scale: 1.1 }}
                              transition={{ duration: 0.7 }}
                            />
                          ) : (
                            <div className="w-full h-full bg-slate-100 flex items-center justify-center">
                              <Globe className="w-12 h-12 text-slate-300" />
                            </div>
                          )}

                          <div className="absolute top-4 left-4">
                            <span className="px-3 py-1 bg-white/90 backdrop-blur-md rounded-full text-xs font-bold text-indigo-700 shadow-lg border border-white/50">
                              {blog.category || 'General'}
                            </span>
                          </div>
                        </div>

                        {/* Content */}
                        <div className="p-6 flex flex-col flex-1">
                          <div className="flex items-center gap-4 text-xs font-semibold text-slate-400 mb-4">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              {formatDate(blog.createdAt)}
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              {Math.ceil((blog.content?.length || 0) / 1000)} min read
                            </div>
                          </div>

                          <h2 className="text-xl font-bold text-slate-800 mb-3 leading-snug group-hover:text-indigo-600 transition-colors">
                            {blog.title}
                          </h2>
                          <p className="text-slate-500 text-sm line-clamp-2 leading-relaxed mb-6 flex-1">
                            {blog.description}
                          </p>

                          <div className="flex items-center justify-between pt-4 border-t border-slate-100 mt-auto">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-pink-400 p-[2px]">
                                <div className="w-full h-full bg-white rounded-full flex items-center justify-center text-[10px] font-bold text-indigo-600">
                                  {blog.author?.substring(0, 2).toUpperCase() || 'AU'}
                                </div>
                              </div>
                              <div className="text-xs font-semibold text-slate-600">By {blog.author}</div>
                            </div>

                            <div className="flex items-center gap-4 text-sm font-semibold">
                              <div className="flex items-center gap-1 text-slate-400 group-hover:text-pink-500 transition-colors">
                                <Heart className="w-4 h-4" /> {blog.likes || 0}
                              </div>
                              <span className="flex items-center gap-1 text-indigo-600 group-hover:translate-x-1 transition-transform">
                                Read <ChevronRight className="w-4 h-4" />
                              </span>
                            </div>
                          </div>
                        </div>
                      </motion.article>
                    </Link>
                  ))}
                </AnimatePresence>
              </motion.div>
            )}

            {!loading && filteredBlogs.length === 0 && (
              <div className="text-center py-24 bg-white rounded-3xl border border-dashed border-slate-200">
                <h3 className="text-xl font-bold text-slate-800 mb-2">No articles found</h3>
                <button onClick={() => { setSearchTerm(''); setSelectedCategory('all'); setFilteredBlogs(blogs) }} className="text-indigo-600 font-semibold hover:underline">Clear Search</button>
              </div>
            )}

            {!loading && totalPages > 1 && (
              <div className="mt-12 flex justify-center gap-2">
                <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} className="px-5 py-2.5 rounded-xl bg-white border border-slate-200 font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">Prev</button>
                <span className="px-5 py-2.5 bg-indigo-50 text-indigo-700 font-bold rounded-xl">{currentPage} of {totalPages}</span>
                <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} className="px-5 py-2.5 rounded-xl bg-white border border-slate-200 font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">Next</button>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default BlogPage;